import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { usuarioService } from '../../services/api';

const TwoFactorAuth: React.FC = () => {
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Obter dados do localStorage em vez da URL
  const usuarioId = localStorage.getItem('2fa_usuario_id');
  const email = localStorage.getItem('2fa_email');
  const senha = localStorage.getItem('2fa_senha');
  
  useEffect(() => {
    // Verificar se os dados necessários estão disponíveis
    if (!usuarioId || !email || !senha) {
      console.error('❌ [2FA] Dados necessários não encontrados no localStorage');
      navigate('/login', { replace: true });
      return;
    }
    
    console.log('🔐 [2FA] Componente carregado com dados:', {
      usuarioId,
      email,
      temSenha: !!senha
    });
  }, [usuarioId, email, senha, navigate]);
  const codigoGerado = localStorage.getItem('2fa_codigo_gerado'); // Para debug apenas
  
  useEffect(() => {
    // Verificar se os dados necessários estão disponíveis
    if (!usuarioId || !email || !senha) {
      console.error('❌ [2FA] Dados necessários não encontrados no localStorage');
      navigate('/login', { replace: true });
      return;
    }
    
    console.log('🔐 [2FA] Dados carregados do localStorage:', {
      usuarioId,
      email,
      temSenha: !!senha,
      codigoGerado: codigoGerado // Apenas para debug
    });
  }, [usuarioId, email, senha, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (codigo.length !== 4) {
      setError('O código deve ter 4 dígitos');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('🔐 [2FA] Verificando código:', { usuarioId, codigo });
  
      // Verificar código 2FA
      const response = await usuarioService.verificarCodigo2FA(usuarioId!, codigo);
      
      console.log('🔐 [2FA] Resposta verificação:', response);
      
      if (response.data?.success) {
        console.log('✅ [2FA] Código válido - fazendo login completo');
        
        // ✅ LIMPAR DADOS 2FA ANTES DO LOGIN
        localStorage.removeItem('2fa_ativo');
        localStorage.removeItem('2fa_codigo_gerado');
        localStorage.removeItem('2fa_usuario_id');
        localStorage.removeItem('2fa_email');
        localStorage.removeItem('2fa_senha');
        
        // Fazer login completo
        const loginSuccess = await login(email!, senha!);
        if (loginSuccess && loginSuccess.success) {
          console.log('✅ [2FA] Login completo realizado com sucesso');
          navigate('/app/dashboard', { replace: true });
        } else {
          setError('Erro ao completar login após verificação 2FA');
        }
      } else {
        setError('Código inválido ou expirado');
      }
    } catch (error) {
      console.error('❌ [2FA] Erro ao verificar código:', error);
      setError('Erro ao verificar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
          Verificação 2FA
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Digite o código de 4 dígitos enviado para seu WhatsApp
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Código 2FA"
            type="text"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            margin="normal"
            required
            inputProps={{ maxLength: 4 }}
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              mb: 2
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Verificar Código'
            )}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/login')}
            disabled={loading}
          >
            Voltar ao Login
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default TwoFactorAuth;
